// Everything this chart draws on top of Highcharts' own render: the hairlines dividing one band
// from the next, the crosshair marking the bucket the readout row is describing, and that bucket's
// per-metric value labels with their hover dots.
//
// One object per chart instance owns every element it creates, so a redraw rebuilds exactly what it
// drew last time and unmounting tears down all of it - rather than three mechanisms each remembering
// their own leftovers.

import type { Chart, Point, SVGElement } from 'highcharts';
import type { BandBox, BandLayout } from './productivityBands';
import { CHART_AXIS_RULE_COLORS, CHART_CHROME_COLORS } from './productivityColors';
import { drawCursorLabels, type CursorLabel } from './productivityCursorLabels';

// A hairline across the full plot width wherever one band ends and the next begins. The draft this
// panel follows draws each of its stacked charts in its own bordered box; a single stacked-axis
// chart cannot do that, and a bare gap reads as spacing rather than as a division - so the rule is
// what carries the draft's separation across. Only real gaps get one: the fused weight/water pair
// (see FUSED_WITH_BAND_ABOVE) is deliberately one continuous box and must not be ruled apart.
//
// Drawn on the chart's own `render` rather than declared as plot lines: a plot line belongs to one
// axis and is placed in that axis' data units, so "the top of the water band" would have to be
// re-derived from the water scale on every redraw and would drift with it. These boundaries are
// geometry, not data.
const BAND_SEPARATOR_MIN_GAP_PX = 8;

// At the top of the gap rather than its middle: the lower band's caption is drawn just above that
// band's own top edge, so a rule above the caption groups the two, while one through the middle
// would cross the caption's text. BAND_GAP_PX is sized to hold both.
const BAND_SEPARATOR_OFFSET_PX = 2;

// The x-axis plot line marking the bucket the readout row currently describes. Removed and re-added
// (rather than moved) on each change, so the id is shared by both calls.
const READOUT_CROSSHAIR_ID = 'productivity-readout-crosshair';

type BandRow = { top: number; bottom: number };

// What the readout row is currently describing, as the chart has to draw it.
export type OverlayCursor = {
    bucketMs: number;
    // Whether the reader is actually pointing at something - hovering, or holding a pin. At rest the
    // row describes the latest reading and says so with its own date, and a full-height line standing
    // there with no cursor on it is read as chrome rather than as a cursor: it lands a few pixels
    // short of the axis, over the right-hand axis numbers, and moves with the granularity (~15px
    // inside the plot at Dag, ~79px at Uge) because it sits on the last bucket holding a reading while
    // the axis ends at the requested range.
    //
    // The value boxes go with it. They earn their place over the data while the reader is pointing -
    // that is the whole argument for them, a number where the eye already is - but at rest they are a
    // white box parked over the newest readings, which are the ones most likely to be looked at, and
    // the readout row above the chart is already carrying those same figures with nothing in front of
    // them. Their dots stay - those mark which readings the row is quoting, and a 7px dot on a curve
    // covers nothing - so what goes at rest is the text, not the label.
    pointing: boolean;
    // A pinned crosshair draws heavier and in the app's own accent rather than the drifting grey one,
    // so the line itself says the values beside it are being held rather than followed.
    pinned: boolean;
    // The points to light up under the cursor, and the value labels to draw beside them. Every
    // labelled point is in `points` too - Water Norm contributes a dot but shares water actual's
    // label, so the two lists are not the same length.
    points: Point[];
    labels: CursorLabel[];
};

export type ProductivityOverlayState = {
    // The bands as the reader sees them. Redrawn on every chart render, the number of boundaries
    // changing with the toggles.
    bands?: BandLayout;
    // Omitted to leave the cursor chrome exactly as it is; null to clear it.
    cursor?: OverlayCursor | null;
};

export type ProductivityOverlay = {
    render(state: ProductivityOverlayState): void;
    // Lets go of the hovered points without touching them, for when the render they belong to has
    // already been thrown away: Highcharts leaves the stripped remains behind rather than nulling
    // the references, and there is no state left on them to clear.
    forgetHoverMarks(): void;
    destroy(): void;
};

// Where a band is, in chart pixels. Taken from the layout rather than from the axes: both context
// axes run with their chrome switched off, and a band that isn't drawing leaves an axis whose top
// and height say nothing about where the reader is.
export function bandBoundsPx(chart: Chart, box: BandBox | null): { top: number; bottom: number } | null {
    if (box === null) return null;
    const toPixels = (percent: number) => chart.plotTop + (percent / 100) * chart.plotHeight;
    return { top: toPixels(box.top), bottom: toPixels(box.top + box.height) };
}

// The bands as the reader sees them, taken from the layout rather than from the axes. Several axes
// share one row (weight with Daily Gain, water with its norm), some rows carry an axis whose chrome
// is switched off entirely - the two context bands label themselves through their own series - and
// a metric withheld at the current granularity hides its axis while its band stays on screen. Read
// off the axes, every one of those cases would move or lose a rule that belongs to a box the reader
// can plainly see.
function bandSeparatorRows(chart: Chart, layout: BandLayout): BandRow[] {
    return (['productionGroups', 'feedMixes', 'weight', 'water', 'growthIndex', 'deadCount'] as const)
        .map((id) => bandBoundsPx(chart, layout[id]))
        .filter((row): row is BandRow => row !== null);
}

export function createProductivityOverlay(chart: Chart): ProductivityOverlay {
    let separators: SVGElement[] = [];
    let cursorLabels: SVGElement[] = [];
    let hoveredPoints: Point[] = [];

    function clearHoverMarks(): void {
        // Guarded: a re-render destroys the points this list is holding, and Highcharts leaves the
        // stripped remains behind rather than nulling the references. Calling setState on one of those
        // throws and takes the whole readout down with it.
        for (const point of hoveredPoints) {
            if (typeof point?.setState === 'function') point.setState('');
        }
        hoveredPoints = [];
        for (const element of cursorLabels) {
            if (typeof element?.destroy === 'function') element.destroy();
        }
        cursorLabels = [];
    }

    function renderBands(layout: BandLayout): void {
        // Destroyed and rebuilt rather than repositioned: `render` fires on every redraw and the
        // number of boundaries changes with the toggles, so survivors would accumulate.
        separators.forEach((element) => element.destroy());

        const rows = bandSeparatorRows(chart, layout);
        const left = chart.plotLeft;
        const right = chart.plotLeft + chart.plotWidth;
        separators = rows
            .map((row, index) => ({ row, previous: rows[index - 1] }))
            .filter(({ row, previous }) => previous !== undefined && row.top - previous.bottom >= BAND_SEPARATOR_MIN_GAP_PX)
            .map(({ previous }) => {
                // The half pixel keeps a 1px stroke crisp.
                const y = Math.round(previous.bottom + BAND_SEPARATOR_OFFSET_PX) + 0.5;
                return chart.renderer
                    .path([
                        ['M', left, y],
                        ['L', right, y],
                    ])
                    .attr({ stroke: CHART_AXIS_RULE_COLORS.bandSeparator, 'stroke-width': 1, zIndex: 1 })
                    .add();
            });
    }

    function renderCursor(cursor: OverlayCursor | null): void {
        const xAxis = chart.xAxis?.[0];
        if (!xAxis) return;

        xAxis.removePlotLine(READOUT_CROSSHAIR_ID);
        if (cursor?.pointing) {
            xAxis.addPlotLine({
                id: READOUT_CROSSHAIR_ID,
                value: cursor.bucketMs,
                color: cursor.pinned ? CHART_CHROME_COLORS.pinnedCrosshair : CHART_CHROME_COLORS.crosshair,
                width: cursor.pinned ? 2 : 1,
                zIndex: 5,
            });
        }

        clearHoverMarks();
        if (cursor === null) return;
        for (const point of cursor.points) {
            point.setState('hover');
            hoveredPoints.push(point);
        }
        cursorLabels = drawCursorLabels(chart, cursor.labels, { text: cursor.pointing });
    }

    return {
        render(state: ProductivityOverlayState): void {
            if (state.bands !== undefined) renderBands(state.bands);
            if (state.cursor !== undefined) renderCursor(state.cursor);
        },
        forgetHoverMarks(): void {
            hoveredPoints = [];
        },
        destroy(): void {
            separators.forEach((element) => element.destroy());
            separators = [];
            clearHoverMarks();
            // `chart` holds whatever instance was last handed over, and the panel swaps the chart out
            // entirely for its loading, empty and failed states - so this can run against an instance
            // that has already been destroyed. A destroyed chart keeps its shape but loses its axes,
            // which is why this tests for the axis rather than the chart.
            chart.xAxis?.[0]?.removePlotLine(READOUT_CROSSHAIR_ID);
        },
    };
}

// One overlay per chart instance, resolved rather than held: the chart's own `render` event fires
// before the instance has been handed to anything that could have created one, and the panel goes on
// using the same instance across every options update.
const overlaysByChart = new WeakMap<Chart, ProductivityOverlay>();

export function productivityOverlay(chart: Chart): ProductivityOverlay {
    const existing = overlaysByChart.get(chart);
    if (existing !== undefined) return existing;
    const created = createProductivityOverlay(chart);
    overlaysByChart.set(chart, created);
    return created;
}
